import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ChevronLeft, TriangleAlert } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FreshnessChip } from '@/components/tracking/FreshnessChip';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { profitFirstAllocation, type ProfitFirstTaps, type ProfitFirstBucket } from '@/lib/financial-calc/forecast';
import { netProfitBeforeTax, netMarginPct } from '@/lib/financial-calc/ratios';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/profit-first — Profit First (F-013 S6.10 / F-007). Allocate real revenue into the four
 * buckets by Target Allocation Percentages. The base is the REAL latest-month revenue; the profit target is
 * compared against your REAL current profit (the one bucket a standard ledger tells us honestly). Owner's-pay
 * and tax aren't cleanly separable from a normal chart of accounts, so we don't invent their current split.
 */
export const Route = createFileRoute('/finance/forecast_/profit-first')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <ProfitFirst />
    </FinanceGuard>
  ),
});

// Profit First starter TAPs (a common small-business band; the builder tunes them).
const DEFAULT_TAPS: ProfitFirstTaps = { profit: 5, ownerPay: 35, tax: 15, opex: 45 };

function ProfitFirst() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const rt = (periods[periods.length - 1]?.roleTotals ?? {}) as Record<string, number>;

  const [taps, setTaps] = useState<ProfitFirstTaps>(DEFAULT_TAPS);
  const setTap = (k: ProfitFirstBucket, v: number) => setTaps((t) => ({ ...t, [k]: v }));

  const base = rt.revenue ?? 0;
  const alloc = profitFirstAllocation(base, taps);

  const real = useMemo(() => {
    const profit = netProfitBeforeTax(rt);
    return { profit, profitPct: netMarginPct(rt) };
  }, [rt]);

  const profitLine = alloc.lines.find((l) => l.key === 'profit')!;
  const profitGap = profitLine.targetAmount - real.profit; // + = redirect this much more into profit

  if (!model || periods.length === 0) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <FinanceNav />
        <div className="mt-8 rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync so we can allocate your real revenue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2"><h1 className="ds-h1">Profit First</h1><FreshnessChip fetchedAt={model?.fetchedAt} /></div>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">Take your profit first — allocate every dollar of revenue on purpose. ({model.period})</p>

      <div className="mb-3.5 rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Base — real revenue this month</p>
          <p className="ds-h3">{fmtCurrency(base)}</p>
        </div>
      </div>

      {!alloc.balanced && (
        <div className="mb-3.5 flex items-start gap-2 rounded-xl border border-warning/60 bg-warning-light/40 px-3 py-2 text-[12px] text-foreground">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span>Your percentages add up to <strong>{alloc.tapSum}%</strong>, not 100%. Adjust them so every dollar is allocated — the amounts below won’t reconcile to your revenue until they do.</span>
        </div>
      )}

      {/* The split at a glance — one bar, every dollar allocated. */}
      <div className="mb-3.5 rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Where each dollar goes</p>
        <div className="flex h-5 overflow-hidden rounded">
          {alloc.lines.map((l, i) => {
            const tones = ['bg-success/70', 'bg-[hsl(var(--primary))]', 'bg-warning/70', 'bg-[hsl(var(--info))]'];
            return <div key={l.key} className={tones[i % tones.length]} style={{ width: `${Math.max(0, l.targetPct)}%` }} title={`${l.label} ${l.targetPct}%`} />;
          })}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[10.5px] text-muted-foreground">
          {alloc.lines.map((l, i) => {
            const dots = ['bg-success/70', 'bg-[hsl(var(--primary))]', 'bg-warning/70', 'bg-[hsl(var(--info))]'];
            return <span key={l.key} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${dots[i % dots.length]}`} />{l.label} {l.targetPct}%</span>;
          })}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-[12.5px]">
          <thead><tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-bold">Bucket</th>
            <th className="px-3 py-2 text-right font-bold">Target %</th>
            <th className="px-3 py-2 text-right font-bold">Target $ / mo</th>
          </tr></thead>
          <tbody>
            {alloc.lines.map((l) => (
              <tr key={l.key} className="border-b border-border/60 last:border-b-0">
                <td className="px-3 py-2 font-semibold text-foreground">{l.label}</td>
                <td className="px-3 py-2 text-right">
                  <div className="ml-auto flex w-24 items-center rounded-md border border-border bg-background px-2">
                    <input type="number" value={l.targetPct} step={1} min={0}
                      onChange={(e) => setTap(l.key, parseFloat(e.target.value) || 0)}
                      className="w-full bg-transparent py-1 text-right text-base sm:text-[12px] text-foreground focus:outline-none" />
                    <span className="text-[11px] text-muted-foreground">%</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCurrency(l.targetAmount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={`border-t border-border text-[11.5px] ${alloc.balanced ? 'text-muted-foreground' : 'text-warning'}`}>
              <td className="px-3 py-2 font-semibold">Total</td>
              <td className="px-3 py-2 text-right font-semibold">{alloc.tapSum}%</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCurrency(alloc.lines.reduce((s, l) => s + l.targetAmount, 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-3.5 rounded-2xl border border-border bg-card p-4">
        <p className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Profit — where you are vs the target</p>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span className="text-[12.5px] text-muted-foreground">Currently: <span className="font-mono tabular-nums text-foreground">{fmtCurrency(real.profit)}</span> ({real.profitPct == null ? '—' : `${real.profitPct.toFixed(1)}%`} of revenue)</span>
          <span className="text-[12.5px] text-muted-foreground">Target: <span className="font-mono tabular-nums text-foreground">{fmtCurrency(profitLine.targetAmount)}</span> ({profitLine.targetPct}%)</span>
        </div>
        <p className={`mt-1.5 text-[12.5px] font-semibold ${profitGap > 0 ? 'text-danger' : 'text-success'}`}>
          {profitGap > 0
            ? `Redirect ${fmtCurrency(profitGap)}/mo into profit to hit the target.`
            : `You're already taking more than the target — ${fmtCurrency(-profitGap)}/mo ahead.`}
        </p>
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">Base and current profit are your real synced figures. We compare profit — the one bucket a standard ledger reports cleanly; owner’s pay and tax aren’t separable from most charts of accounts, so those show the target to aim for, not a current split. Percentages are a planning method, not tax advice.</p>
      <ForecastToolNav current="profit-first" />
    </div>
  );
}
